import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

// Billing timestamps — a trial's end, a period's end, the end of a grace period
// — are UTC. Since 2026-10-16 the API writes every one with its zone
// ("2026-10-24T09:30:00Z"), and a value with a Z or an offset is read here with
// its own zone, so nothing is shifted twice. Some answers before that came
// without one ("2026-10-24T09:30:00"), which `new Date(...)` and plain
// `dayjs(...)` read as the browser's local time; such a value is still read as
// UTC, never as local time.

/** A billing timestamp as a UTC dayjs, or null when there isn't a valid one. */
export const parseBillingDate = (value) => {
   if (!value) return null;

   const date = dayjs.utc(value);
   return date.isValid() ? date : null;
};

/** A billing timestamp in the reader's own time zone, or null. */
export const formatBillingDate = (value, format = "MMM D, YYYY") => {
   const date = parseBillingDate(value);
   return date ? date.local().format(format) : null;
};

/**
 * A billing moment, date and time, in the reader's own locale and time zone —
 * or null. For the moments where the hour matters, like the end of a grace
 * period, which falls whenever the payment failed rather than at midnight.
 * `locale` and `timeZone` are the browser's unless given; they are there so a
 * test can pin them.
 */
export const formatBillingDateTime = (value, { locale, timeZone } = {}) => {
   const date = parseBillingDate(value);
   if (!date) return null;

   return new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short", timeZone }).format(
      date.toDate()
   );
};

/** True when a billing timestamp is still ahead. */
export const isBillingDateAhead = (value, now = Date.now()) => {
   const date = parseBillingDate(value);
   return Boolean(date) && date.valueOf() > now;
};
